import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import axiosInstance from '../../api/axiosInstance';
import { dateFormat } from '../../utils/dateFormat';
import DeleteConfirmationModal from '../test-plans/DeleteConfirmationModal';
import DropdownMenu from './DropdownMenu';
import { MdOutlineHeight } from 'react-icons/md';
// import '../../styles/projects.css';

// import DeleteConfirmationModal from '../test-plans/DeleteConfirmationModal';

const STATUS_LABELS = {
  0: 'Active',
  1: 'Inactive',
}; 


function TestDataList() {
  const navigate = useNavigate();
  const { project_id } = useParams();
  const location = useLocation();
  
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const dropdownRef = useRef(null);
  // map of refs for each profile's trigger button so DropdownMenu can position relative to it
  const triggerRefs = useRef({});

  const getTriggerRef = (id) => {
    if (!triggerRefs.current[id]) triggerRefs.current[id] = React.createRef();
    return triggerRefs.current[id];
  };

  const fetchProfiles = useCallback(async (id) => {
    if (!id) return setError('Project id missing in query params');
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/list_test_profiles?project_id=${id}`);
      // axiosInstance response interceptor returns response.data, so res should be the API object
      const data = res?.profiles || [];
      setProfiles(data);
    } catch (err) {
      console.error('Failed to fetch test profiles', err);
      setError(err?.message || 'Failed to fetch test profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(selectedProfile && selectedProfile.status == '1'){
        handleConfirmStatusChange();
    }
    else{
    fetchProfiles(project_id);
    }
  }, [fetchProfiles, project_id, location,selectedProfile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdd = () => {
    if (!project_id) return setError('Project id missing for navigation');
    navigate(`/dashboard/project-test-data/${project_id}`);
  };

  const handleEdit = (profileId) => {
    console.log("inside edit");
    

    setActiveDropdown(null);
    navigate(`/dashboard/project-test-data/${project_id}?profile_id=${profileId}`);
  };

  const handleOpenDeleteModal = (profile) => {
    setSelectedProfile(profile);
    setIsDeleteModalOpen(true);
    setActiveDropdown(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedProfile(null);
  };

  const handleConfirmStatusChange = async () => {
    console.log("inside confirm",selectedProfile);
    
    if (!selectedProfile ) return;

    setIsUpdating(true);
    try {
      const newStatus = selectedProfile.status === 0 ? 1 : 0;
      
      await axiosInstance.put(`/activate_test_profile?profile_id=${selectedProfile.id}&active_status=${newStatus}`);
      // Refresh the profiles list after successful update
    //   await fetchProfiles(project_id);
      handleCloseDeleteModal();
    } catch (err) {
      setError(err?.message || 'Failed to update profile status');
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleDropdown = (profileId, e) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === profileId ? null : profileId);
  };

  const buildDropdownItems = (profile) => {
    return [
      {
        label: 'Edit',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        ),
        onClick: () => handleEdit(profile.id),
      },
      { type: 'divider' },
      profile.status === 0
        ? {
            label: 'Delete',
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            ),
            onClick: () => handleOpenDeleteModal(profile),
            className: 'text-red-600',
          }
        : {
            label: 'Activate',
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),

            onClick: () => {
                setSelectedProfile(profile);
            },
            className: 'text-green-600',
          },
    ];
  };

  return (
    <div className="w-full p-4 bg-white rounded-md shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Test Profiles</h2>
        <div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm"
            data-testid="add-test-data-btn"
          >
            Add test Data
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600">{error}</div>
      )}

      <div className="projects-table-container overflow-x-auto" style={({ minHeight: '40px' })}>
        <table className="projects-table w-full">
          <thead>
            <tr>
              <th className="">Test Profile Name</th>
              <th style={{ textAlign: 'center' }}>Created By</th>
              <th style={{ textAlign: 'center' }}>Created At</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="table-row">
                <td colSpan={5} className="table-cell text-center py-6 text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : profiles.length === 0 ? (
              <tr className="table-row">
                <td colSpan={5} className="table-cell text-center py-6 text-gray-500">No test profiles found.</td>
              </tr>
            ) : (
              profiles.map((profile) => (
                <tr key={profile.id} className="table-row hover:table-row-hover">
                  <td className="table-cell" style={{ textAlign: "center !important" }}>{profile.test_profile_name}</td>
                  <td className="!text-center" style={{ textAlign: "center !important" }}>{profile.created_by_name}</td>
                  <td className="table-cell text-center" style={{ textAlign: "center !important" }}>{dateFormat(profile.created_at)}</td>
                  <td className="table-cell text-center" style={{ textAlign: "center !important" }}>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${profile.status === 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {STATUS_LABELS[profile.status] ?? 'Unknown'}
                    </span>
                  </td>
                  <td className="table-cell text-center">
                    <div className="action-btn-container inline-block" >
                      <button
                        ref={getTriggerRef(profile.id)}
                        onClick={(e) => toggleDropdown(profile.id, e)}
                        className="action-btn more inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600"
                        aria-label="Actions"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10.5 1.5H9.5V3.5H10.5V1.5ZM10.5 8.5H9.5V10.5H10.5V8.5ZM10.5 15.5H9.5V17.5H10.5V15.5Z" />
                        </svg>
                      </button>

                      <DropdownMenu
                        isOpen={activeDropdown === profile.id}
                        onClose={() => setActiveDropdown(null)}
                        items={buildDropdownItems(profile)}
                        triggerRef={getTriggerRef(profile.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        itemName={selectedProfile?.test_profile_name}
        itemType="test profile"
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmStatusChange}
      />
    </div>
  );
}

export default TestDataList;